/**
 * WeaveIn.ai Platform - Request Signature (Full Page, 3-Step Flow)
 * Step 1: Upload document (PDF)
 * Step 2: Place signing fields on the document (drag-and-drop)
 * Step 3: Signer details + send method
 */

import React, { useState, useRef } from 'react';
import {
  Send, Mail, Smartphone, User, Users, FileText, Clock,
  Link2, Copy, Check, X, PenTool, Upload, ChevronRight, ChevronLeft,
  ArrowLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/ui/PhoneInput';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { config } from '@/src/config';
import { lazyWithRetry } from '@/lib/lazyWithRetry';

const DocumentPrepEditor = lazyWithRetry(() => import('./DocumentPrepEditor').then(m => ({ default: m.DocumentPrepEditor })));
import type { SigningField, SignerInfo } from './DocumentPrepEditor';

interface RequestSignaturePageProps {
  onBack?: () => void;
  recipientName?: string;
  recipientEmail?: string;
  recipientPhone?: string;
  recipientMemberId?: number;
  recipientUserId?: number;
  docRecordId?: number;
  formInstanceId?: number;
  workflowStepInstanceId?: number;
  defaultTitle?: string;
  defaultMessage?: string;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
};

export function RequestSignaturePage({
  onBack,
  recipientName = '', recipientEmail = '', recipientPhone = '',
  recipientMemberId, recipientUserId,
  docRecordId, formInstanceId, workflowStepInstanceId,
  defaultTitle = '', defaultMessage = '',
}: RequestSignaturePageProps) {
  const [step, setStep] = useState(1);

  // Step 1: Document
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState<string | null>(null);
  const [existingDocs, setExistingDocs] = useState<any[]>([]);
  const [selectedExistingDocId, setSelectedExistingDocId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 2: Fields
  const [signingFields, setSigningFields] = useState<SigningField[]>([]);

  // Step 3: Signer details
  const [title, setTitle] = useState(defaultTitle);
  const [message, setMessage] = useState(defaultMessage);
  const [signerName, setSignerName] = useState(recipientName);
  const [signerEmail, setSignerEmail] = useState(recipientEmail);
  const [signerPhone, setSignerPhone] = useState(recipientPhone);
  const [signerRole, setSignerRole] = useState('member');
  const [signerTypeValue, setSignerTypeValue] = useState(recipientMemberId ? 'internal' : 'external');
  const [sendVia, setSendVia] = useState<'email' | 'sms' | 'link'>('email');
  const [expiresHours, setExpiresHours] = useState(168);
  const [docSigners, setDocSigners] = useState<SignerInfo[]>([]);
  const [signingMode, setSigningMode] = useState<'sequential' | 'simultaneous'>('simultaneous');

  // State
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [signingUrl, setSigningUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Fetch existing documents for this member
  React.useEffect(() => {
    if (!docRecordId && recipientMemberId) {
      fetch(`${config.weaveinApi.baseUrl}/api/documents/member/${recipientMemberId}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      })
        .then(r => r.ok ? r.json() : [])
        .then(docs => setExistingDocs(Array.isArray(docs) ? docs : []))
        .catch(() => {});
    }
  }, [recipientMemberId, docRecordId]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocumentFile(file);
    setDocumentPreviewUrl(URL.createObjectURL(file));
  };

  // Merge signers from DocumentPrepEditor with single-signer fallback
  const allSigners: SignerInfo[] = docSigners.length > 0
    ? docSigners
    : [{ order: 1, name: signerName, email: signerEmail, phone: signerPhone, role: signerRole, type: signerTypeValue }];

  const updateSignerInList = (idx: number, updates: Partial<SignerInfo>) => {
    if (docSigners.length > 0) {
      const updated = [...docSigners];
      updated[idx] = { ...updated[idx], ...updates };
      setDocSigners(updated);
    } else {
      // Update single signer state
      if (updates.name !== undefined) setSignerName(updates.name);
      if (updates.email !== undefined) setSignerEmail(updates.email);
      if (updates.phone !== undefined) setSignerPhone(updates.phone);
      if (updates.role !== undefined) setSignerRole(updates.role);
      if (updates.type !== undefined) setSignerTypeValue(updates.type);
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) return;
    // Validate at least first signer has a name
    const primary = allSigners[0];
    if (!primary?.name?.trim()) return;

    // Validate contact info matches delivery method
    if (sendVia === 'email') {
      const missingEmail = allSigners.find(s => !s.email?.trim());
      if (missingEmail) {
        alert(`Please enter an email address for ${missingEmail.name || 'the signer'} to send via email.`);
        return;
      }
    }
    if (sendVia === 'sms') {
      const missingPhone = allSigners.find(s => !s.phone?.trim());
      if (missingPhone) {
        alert(`Please enter a phone number for ${missingPhone.name || 'the signer'} to send via SMS.`);
        return;
      }
    }

    setSubmitting(true);

    try {
      let data: any;

      if (allSigners.length > 1 && signingMode === 'sequential') {
        // Sequential multi-party signing (1 → 2 → 3)
        const res = await fetch(`${config.weaveinApi.baseUrl}/api/signatures/multi-party`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            title,
            message: message || undefined,
            doc_record_id: docRecordId || selectedExistingDocId || undefined,
            form_instance_id: formInstanceId || undefined,
            workflow_step_instance_id: workflowStepInstanceId || undefined,
            expires_in_hours: expiresHours,
            signers: allSigners.map(s => ({
              name: s.name, email: s.email, phone: s.phone, role: s.role, type: s.type || 'external',
            })),
          }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
        const multiData = await res.json();
        data = multiData.signing_requests?.[0] || multiData;
        data.signing_url = multiData.signing_requests?.[0]?.signing_url;
        (data as any)._multiRequests = multiData.signing_requests || [];
      } else if (allSigners.length > 1 && signingMode === 'simultaneous') {
        // Simultaneous — create independent requests for each signer, all sent at once
        const allResults = [];
        for (const s of allSigners) {
          const res = await fetch(`${config.weaveinApi.baseUrl}/api/signatures/requests`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({
              title, message: message || undefined,
              signer_type: s.type === 'staff' ? 'internal' : s.type || 'external', signer_name: s.name,
              signer_email: s.email || undefined, signer_phone: s.phone || undefined,
              signer_role: s.role || 'member',
              doc_record_id: docRecordId || selectedExistingDocId || undefined,
              expires_in_hours: expiresHours,
              signing_fields: signingFields.filter(f => f.signer_order === s.order),
            }),
          });
          if (res.ok) allResults.push(await res.json());
        }
        data = allResults[0] || {};
        data.signing_url = allResults[0]?.signing_url;
        (data as any)._allResults = allResults;
      } else {
        // Single signer
        const res = await fetch(`${config.weaveinApi.baseUrl}/api/signatures/requests`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            title,
            message: message || undefined,
            signer_type: primary.type === 'staff' ? 'internal' : primary.type || 'external',
            signer_name: primary.name,
            signer_email: primary.email || undefined,
            signer_phone: primary.phone || undefined,
            signer_role: primary.role || 'member',
            signer_user_id: recipientUserId || undefined,
            signer_member_id: recipientMemberId || undefined,
            doc_record_id: docRecordId || selectedExistingDocId || undefined,
            form_instance_id: formInstanceId || undefined,
            workflow_step_instance_id: workflowStepInstanceId || undefined,
            expires_in_hours: expiresHours,
            send_via: sendVia,
            signing_fields: signingFields.length > 0 ? signingFields : undefined,
          }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
        data = await res.json();
      }

      // Upload document
      if (documentFile) {
        const formData = new FormData();
        formData.append('file', documentFile);
        await fetch(`${config.weaveinApi.baseUrl}/api/signatures/requests/${data.id}/document`, {
          method: 'POST', credentials: 'include', headers: getAuthHeaders(), body: formData,
        }).catch(() => {});
      }

      // Save fields
      if (signingFields.length > 0) {
        await fetch(`${config.weaveinApi.baseUrl}/api/signatures/requests/${data.id}/fields`, {
          method: 'PUT', credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ signing_fields: signingFields }),
        }).catch(() => {});
      }

      // Send emails/SMS to ALL signers
      if (sendVia !== 'link') {
        const requestsToSend: number[] = [];

        if (allSigners.length > 1 && signingMode === 'simultaneous' && (data as any)._allResults) {
          // Simultaneous: send to each created request
          for (const r of (data as any)._allResults) {
            if (r?.id) requestsToSend.push(r.id);
          }
        } else if (allSigners.length > 1 && signingMode === 'sequential' && (data as any)._multiRequests) {
          // Sequential: only send to first signer
          const first = (data as any)._multiRequests[0];
          if (first?.id) requestsToSend.push(first.id);
        } else if (data?.id) {
          // Single signer
          requestsToSend.push(data.id);
        }

        for (const reqId of requestsToSend) {
          await fetch(`${config.weaveinApi.baseUrl}/api/signatures/requests/${reqId}/send`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ send_via: sendVia }),
          }).catch((e) => console.warn(`Send failed for request ${reqId}:`, e));
        }
      }

      setSigningUrl(data.signing_url);
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const copyUrl = () => {
    if (signingUrl) {
      navigator.clipboard.writeText(signingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const canAdvance = () => {
    if (step === 1) return true;
    if (step === 2) return true;
    if (step === 3) {
      const hasTitle = !!title.trim();
      const hasSigners = allSigners.length > 0 && !!allSigners[0]?.name?.trim();
      if (!hasTitle || !hasSigners) return false;
      // Validate contact info for delivery method
      if (sendVia === 'email') {
        return allSigners.every(s => !!s.email?.trim());
      }
      if (sendVia === 'sms') {
        return allSigners.every(s => !!s.phone?.trim());
      }
      return true; // 'link' mode needs no contact info
    }
    return false;
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        {onBack && (
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
        )}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PenTool className="w-6 h-6 text-blue-600" />
            Request Signature
          </h1>
          <p className="text-sm text-muted-foreground">
            Upload a document, place signing fields, and send for signature.
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-4 mb-8">
        {[
          { num: 1, label: 'Upload Document', icon: Upload },
          { num: 2, label: 'Place Fields', icon: PenTool },
          { num: 3, label: 'Send', icon: Send },
        ].map((s, idx) => (
          <React.Fragment key={s.num}>
            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                s.num === step
                  ? 'bg-blue-600 text-white shadow-md'
                  : s.num < step
                    ? 'bg-green-100 text-green-700 cursor-pointer hover:bg-green-200'
                    : 'bg-gray-100 text-gray-400'
              }`}
              onClick={() => s.num < step && setStep(s.num)}
              disabled={s.num > step}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                s.num < step ? 'bg-green-600 text-white' : s.num === step ? 'bg-white/20' : ''
              }`}>
                {s.num < step ? <Check className="w-4 h-4" /> : s.num}
              </div>
              <s.icon className="w-4 h-4" />
              <span className="font-medium text-sm">{s.label}</span>
            </button>
            {idx < 2 && <div className={`h-0.5 flex-1 ${s.num < step ? 'bg-green-400' : 'bg-gray-200'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Success state */}
      {signingUrl ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-green-700 mb-2">Signature Request Created!</h2>
            <p className="text-muted-foreground mb-6">
              {sendVia === 'link' ? 'Share the link below with the signer.' : `Sent to ${signerName} via ${sendVia.toUpperCase()}.`}
            </p>
            <div className="max-w-lg mx-auto">
              <Label className="text-xs">Signing URL</Label>
              <div className="flex gap-2 mt-1">
                <Input value={signingUrl} readOnly className="text-xs font-mono" />
                <Button variant="outline" onClick={copyUrl}>
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-center">
              <Button variant="outline" onClick={onBack}>Back to Signatures</Button>
              <Button onClick={() => { setSigningUrl(null); setStep(1); setDocumentFile(null); setDocumentPreviewUrl(null); setSigningFields([]); }}>
                Send Another
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* STEP 1: Upload Document */}
          {step === 1 && (
            <Card>
              <CardContent className="py-8">
                <div
                  className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                    dragOver ? 'border-blue-500 bg-blue-50 scale-[1.01]' : 'hover:border-blue-400 hover:bg-blue-50/50'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOver(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) {
                      setDocumentFile(file);
                      setDocumentPreviewUrl(URL.createObjectURL(file));
                    }
                  }}
                >
                  {documentFile ? (
                    <div>
                      <FileText className="w-16 h-16 text-blue-600 mx-auto mb-3" />
                      <p className="text-lg font-semibold">{documentFile.name}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {(documentFile.size / 1024 / 1024).toFixed(1)} MB · Click or drop to replace
                      </p>
                      <Badge className="mt-2 bg-green-100 text-green-800">
                        <Check className="w-3 h-3 mr-1" /> Document selected
                      </Badge>
                    </div>
                  ) : dragOver ? (
                    <div>
                      <Upload className="w-16 h-16 text-blue-500 mx-auto mb-3 animate-bounce" />
                      <p className="text-lg font-semibold text-blue-600">Drop your file here</p>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                      <p className="text-lg font-semibold text-gray-600">Drag & Drop or Click to Upload</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        PDF, DOC, DOCX, JPG, PNG · Max 50 MB
                      </p>
                      <p className="text-xs text-muted-foreground mt-3">
                        Or skip this step to send a signature request without a document.
                      </p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {/* Select from existing documents */}
                {existingDocs.length > 0 && !documentFile && (
                  <div className="mt-6 border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Or select an existing document:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      {existingDocs.map((doc: any) => (
                        <button
                          key={doc.id}
                          className={`flex items-center gap-2 p-2 rounded-lg border text-left text-sm transition-colors ${
                            selectedExistingDocId === doc.id
                              ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                          }`}
                          onClick={() => {
                            setSelectedExistingDocId(doc.id);
                            setDocumentFile(null);
                            setDocumentPreviewUrl(null);
                          }}
                        >
                          <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{doc.document_name || doc.file_name || 'Untitled'}</p>
                            <p className="text-xs text-muted-foreground">{doc.document_type} · {doc.upload_date || doc.created_ts?.split('T')[0] || ''}</p>
                          </div>
                          {selectedExistingDocId === doc.id && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* STEP 2: Place Fields */}
          {step === 2 && (
            <Card>
              <CardContent className="py-6">
                {documentPreviewUrl ? (
                  <React.Suspense fallback={
                    <div className="flex items-center justify-center h-96">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                      <span className="ml-3 text-muted-foreground">Loading document viewer...</span>
                    </div>
                  }>
                    <DocumentPrepEditor
                      fileUrl={documentPreviewUrl}
                      fileType={documentFile?.type}
                      fields={signingFields}
                      onFieldsChange={setSigningFields}
                      signers={docSigners}
                      onSignersChange={setDocSigners}
                    />
                  </React.Suspense>
                ) : (
                  <div className="text-center py-16">
                    <PenTool className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                    <p className="text-lg font-semibold text-gray-600">No Document Uploaded</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Skip this step — the signer will use the signature pad directly.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* STEP 3: Review Signers + Send — Row Layout */}
          {step === 3 && (
            <div className="space-y-6">
              {/* Row 1: Request Details */}
              <Card>
                <CardContent className="py-5 space-y-4">
                  <h3 className="font-semibold text-lg border-b pb-2">Request Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Title *</Label>
                      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Provider Agreement, Care Plan Signature" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Expires In</Label>
                        <Select value={String(expiresHours)} onValueChange={(v) => setExpiresHours(parseInt(v))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="24">24 hours</SelectItem>
                            <SelectItem value="72">3 days</SelectItem>
                            <SelectItem value="168">7 days</SelectItem>
                            <SelectItem value="336">14 days</SelectItem>
                            <SelectItem value="720">30 days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Send Via</Label>
                        <Select value={sendVia} onValueChange={(v) => setSendVia(v as any)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="sms">SMS</SelectItem>
                            <SelectItem value="link">Copy Link</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label>Message to Signers</Label>
                    <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Please review and sign the attached document..." rows={2} />
                  </div>
                </CardContent>
              </Card>

              {/* Row 2: Signers */}
              <Card>
                <CardContent className="py-5 space-y-4">
                  <div className="flex items-center justify-between border-b pb-2">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Users className="w-5 h-5" /> Signers ({allSigners.length})
                    </h3>
                    {allSigners.length > 1 && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">Signing Order:</span>
                        <button className={`px-3 py-1 rounded text-xs font-medium transition-colors ${signingMode === 'simultaneous' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`} onClick={() => setSigningMode('simultaneous')}>All at once</button>
                        <button className={`px-3 py-1 rounded text-xs font-medium transition-colors ${signingMode === 'sequential' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`} onClick={() => setSigningMode('sequential')}>Sequential</button>
                      </div>
                    )}
                  </div>

                  {allSigners.map((s, idx) => (
                    <div key={s.order} className="border rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: ['#3b82f6','#ef4444','#22c55e','#a855f7','#f59e0b'][idx] || '#6b7280' }}>
                          {s.order}
                        </div>
                        <span className="font-semibold">{s.name || `Signer ${s.order}`}</span>
                        <Badge variant="outline" className="text-xs">{s.role || 'member'}</Badge>
                        {signingFields.filter(f => f.signer_order === s.order).length > 0 && (
                          <span className="text-xs text-muted-foreground ml-auto">{signingFields.filter(f => f.signer_order === s.order).length} field(s)</span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        <div>
                          <Label className="text-xs">Full Name *</Label>
                          <Input value={s.name} onChange={(e) => updateSignerInList(idx, { name: e.target.value })} className="h-8" placeholder="Full legal name" />
                        </div>
                        <div>
                          <Label className="text-xs">Email {sendVia === 'email' && <span className="text-red-500">*</span>}</Label>
                          <Input
                            value={s.email || ''}
                            onChange={(e) => updateSignerInList(idx, { email: e.target.value })}
                            className={`h-8 ${sendVia === 'email' && !s.email?.trim() ? 'border-red-300' : ''}`}
                            type="email"
                            placeholder="email@example.com"
                          />
                          {sendVia === 'email' && !s.email?.trim() && (
                            <p className="text-xs text-red-500 mt-0.5">Required for email delivery</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs">Phone (SMS) {sendVia === 'sms' && <span className="text-red-500">*</span>}</Label>
                          <PhoneInput
                            value={s.phone || ''}
                            onChange={(v) => updateSignerInList(idx, { phone: v })}
                            className={`h-8 ${sendVia === 'sms' && !s.phone?.trim() ? 'border-red-300' : ''}`}
                            placeholder="+1 (555) 000-0000"
                          />
                          {sendVia === 'sms' && !s.phone?.trim() && (
                            <p className="text-xs text-red-500 mt-0.5">Required for SMS delivery</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs">Signer Type</Label>
                          <Select value={s.type || 'external'} onValueChange={(v) => updateSignerInList(idx, { type: v })}>
                            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="internal">Internal (Staff)</SelectItem>
                              <SelectItem value="external">External</SelectItem>
                              <SelectItem value="parent">Parent</SelectItem>
                              <SelectItem value="guardian">Guardian</SelectItem>
                              <SelectItem value="legal_representative">Legal Representative</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Role</Label>
                          <Select value={s.role || 'member'} onValueChange={(v) => updateSignerInList(idx, { role: v })}>
                            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {(s.type === 'parent' || s.type === 'guardian' || s.type === 'legal_representative') ? (
                                <>
                                  <SelectItem value="parent">Parent</SelectItem>
                                  <SelectItem value="guardian">Guardian</SelectItem>
                                  <SelectItem value="legal_representative">Legal Representative</SelectItem>
                                </>
                              ) : s.type === 'internal' || s.type === 'staff' ? (
                                <>
                                  <SelectItem value="staff">Staff</SelectItem>
                                  <SelectItem value="supervisor">Supervisor</SelectItem>
                                  <SelectItem value="provider">Provider</SelectItem>
                                </>
                              ) : (
                                <>
                                  <SelectItem value="member">Member</SelectItem>
                                  <SelectItem value="client">Client</SelectItem>
                                  <SelectItem value="provider">Provider</SelectItem>
                                  <SelectItem value="partner">Partner</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Row 3: Summary */}
              <Card className="bg-blue-50/30 border-blue-200">
                <CardContent className="py-4">
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    {documentFile && (
                      <div className="flex items-center gap-1.5">
                        <FileText className="w-4 h-4 text-blue-600" />
                        <span>{documentFile.name}</span>
                      </div>
                    )}
                    {signingFields.length > 0 && (
                      <div className="flex items-center gap-1.5">
                        <PenTool className="w-4 h-4 text-purple-600" />
                        <span>{signingFields.length} field(s)</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <Users className="w-4 h-4 text-green-600" />
                      <span>{allSigners.length} signer(s)</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <span>{expiresHours < 48 ? `${expiresHours}h` : `${Math.round(expiresHours / 24)}d`}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Send className="w-4 h-4 text-blue-600" />
                      <span>{sendVia.toUpperCase()}</span>
                    </div>
                    {allSigners.length > 1 && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">{signingMode === 'sequential' ? '1→2→3' : 'All at once'}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Navigation bar */}
          <div className="flex justify-between mt-6 pt-4 border-t">
            <div>
              {step > 1 && (
                <Button variant="outline" size="lg" onClick={() => setStep(step - 1)}>
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              )}
            </div>
            <div className="flex gap-3">
              {step < 3 ? (
                <Button size="lg" onClick={() => setStep(step + 1)}>
                  {step === 1 && !documentFile ? 'Skip to Fields' : step === 2 && !signingFields.length ? 'Skip to Send' : 'Next Step'}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  size="lg"
                  onClick={handleSubmit}
                  disabled={!canAdvance() || submitting}
                  variant={!canAdvance() || submitting ? 'secondary' : 'primary'}
                  style={!canAdvance() || submitting ? { backgroundColor: '#d1d5db', color: '#6b7280', cursor: 'not-allowed', opacity: 0.7 } : {}}
                >
                  {submitting ? 'Sending...' : (
                    <><Send className="w-4 h-4 mr-2" /> {sendVia === 'link' ? 'Generate Signing Link' : `Send via ${sendVia.toUpperCase()}`}</>
                  )}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
